import React from 'react';

/**
 * Error Boundary Component to catch React errors gracefully
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        
        // Log to console in development only
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
        
        // In production, you could send this to an error reporting service
        // Example: Sentry.captureException(error);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        // Optionally reload the page or reset app state
        if (this.props.onReset) {
            this.props.onReset();
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <div style={styles.iconContainer}>
                            <span style={styles.icon}>⚠️</span>
                        </div>
                        <h2 style={styles.title}>Oops! Something went wrong</h2>
                        <p style={styles.message}>
                            We encountered an unexpected error. Don't worry, your data is safe.
                        </p>
                        
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Error Details (Dev Only)</summary>
                                <pre style={styles.errorText}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                        
                        <div style={styles.actions}>
                            <button onClick={this.handleReset} style={styles.primaryButton}>
                                Try Again
                            </button>
                            <button onClick={() => window.location.href = '/'} style={styles.secondaryButton}>
                                Go to Home
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '20px'
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        textAlign: 'center'
    },
    iconContainer: {
        marginBottom: '20px'
    },
    icon: {
        fontSize: '4rem'
    },
    title: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: '#1e293b',
        marginBottom: '12px'
    },
    message: {
        fontSize: '1.1rem',
        color: '#64748b',
        marginBottom: '30px',
        lineHeight: '1.6'
    },
    details: {
        textAlign: 'left',
        marginBottom: '30px',
        padding: '15px',
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
    },
    summary: {
        cursor: 'pointer',
        fontWeight: '600',
        color: '#475569',
        marginBottom: '10px'
    },
    errorText: {
        fontSize: '0.85rem',
        color: '#dc2626',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflow: 'auto',
        maxHeight: '200px'
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap'
    },
    primaryButton: {
        padding: '12px 32px',
        background: 'linear-gradient(135deg, #27b05b, #16a34a)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'transform 0.2s'
    },
    secondaryButton: {
        padding: '12px 32px',
        background: 'white',
        color: '#27b05b',
        border: '2px solid #27b05b',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'transform 0.2s'
    }
};

export default ErrorBoundary;
